import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import { getConnections, getModels, getModelFields } from '../../api/api';

function ModelsViewer() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [fields, setFields] = useState([]);
  const [searchModel, setSearchModel] = useState('');
  const [loading, setLoading] = useState({
    connections: false,
    models: false,
    fields: false
  });
  const [alert, setAlert] = useState({ show: false, type: 'error', message: '' });

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      loadModels();
    }
  }, [selectedConnection]);

  useEffect(() => {
    if (selectedModel) {
      loadFields();
    }
  }, [selectedModel]);

  useEffect(() => {
    if (searchModel) {
      const filtered = models.filter(model =>
        model.model.toLowerCase().includes(searchModel.toLowerCase()) ||
        model.name.toLowerCase().includes(searchModel.toLowerCase())
      );
      setFilteredModels(filtered);
    } else {
      setFilteredModels(models);
    }
  }, [searchModel, models]);

  const loadConnections = async () => {
    setLoading(prev => ({ ...prev, connections: true }));
    try {
      const response = await getConnections();
      setConnections(response.data.data);
    } catch (error) {
      showAlert('error', error.response?.data?.error || 'Error loading connections');
    } finally {
      setLoading(prev => ({ ...prev, connections: false }));
    }
  };

  const loadModels = async () => {
    setLoading(prev => ({ ...prev, models: true }));
    setModels([]);
    setFilteredModels([]);
    setSelectedModel('');
    setFields([]);
    try {
      const response = await getModels(selectedConnection);
      setModels(response.data.data);
      setFilteredModels(response.data.data);
    } catch (error) {
      showAlert('error', error.response?.data?.error || 'Error loading models');
    } finally {
      setLoading(prev => ({ ...prev, models: false }));
    }
  };

  const loadFields = async () => {
    setLoading(prev => ({ ...prev, fields: true }));
    setFields([]);
    try {
      const response = await getModelFields(selectedConnection, selectedModel);
      setFields(response.data.data);
    } catch (error) {
      showAlert('error', error.response?.data?.error || 'Error loading fields');
    } finally {
      setLoading(prev => ({ ...prev, fields: false }));
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'error', message: '' }), 5000);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('models.title')}
      </Typography>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>{t('models.selectConnection')}</InputLabel>
            <Select
              value={selectedConnection}
              onChange={(e) => setSelectedConnection(e.target.value)}
              disabled={loading.connections}
            >
              {connections.map((conn) => (
                <MenuItem key={conn.id} value={conn.id}>
                  {conn.name} ({conn.url})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('models.search')}
            value={searchModel}
            onChange={(e) => setSearchModel(e.target.value)}
            disabled={!selectedConnection || loading.models}
          />
        </Grid>

        {selectedConnection && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t('models.selectModel')}</InputLabel>
              <Select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={loading.models}
              >
                {filteredModels.map((model) => (
                  <MenuItem key={model.model} value={model.model}>
                    {model.name} ({model.model})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {loading.models && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {selectedModel && !loading.models && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('fields.title')} - {selectedModel}
            </Typography>
            {loading.fields ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : Object.keys(fields).length === 0 ? (
              <Typography color="text.secondary" align="center" p={3}>
                {t('fields.noFields')}
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('fields.showing')}: {Object.keys(fields).length} {t('fields.total')}
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('fields.fieldName')}</strong></TableCell>
                        <TableCell><strong>{t('fields.displayName')}</strong></TableCell>
                        <TableCell><strong>{t('fields.type')}</strong></TableCell>
                        <TableCell><strong>{t('fields.required')}</strong></TableCell>
                        <TableCell><strong>{t('fields.readonly')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(fields).map(([fieldName, fieldInfo]) => (
                        <TableRow key={fieldName}>
                          <TableCell>{fieldName}</TableCell>
                          <TableCell>{fieldInfo.string || fieldName}</TableCell>
                          <TableCell>{fieldInfo.type}</TableCell>
                          <TableCell>{fieldInfo.required ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{fieldInfo.readonly ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      )}}
    </Box>
  );
}

export default ModelsViewer;
